import { Component, OnInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

import { AvaliacaoService, AvaliacaoPublica } from '../../service/avaliacao.service';
import { QuartoService, Quarto } from '../../service/quarto.service';
import { CheckoutService, ReservaCalendario } from '../../service/checkout.service';
import { AnalyticsService } from '../../service/analytics.service';
// Ajuste somente o caminho deste import se o serviço estiver em outra pasta.
import { RegrasMinimoNoitesService, RegraMinimoNoites } from '../../service/RegraMinimoNoites.service';

interface DiaCalendario {
  numero: number;
  data: Date;
  mesAtual: boolean;
  hoje: boolean;
  selecionado: boolean;
  intervalo: boolean;
  desabilitado: boolean;
}

@Component({
  selector: 'app-chale-santorini',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chale-santorini.component.html',
  styleUrl: './chale-santorini.component.css'
})
export class ChaleSantoriniComponent implements OnInit {
  readonly quartoId = 2;
  quarto: Quarto | null = null;
  avaliacoes: AvaliacaoPublica[] = [];
  reservasOcupadas: ReservaCalendario[] = [];
  regrasMinimoNoites: RegraMinimoNoites[] = [];
  carregandoCalendario = true;
  erroCalendario = false;

  imagensCabana: string[] = [
    '/nova-chale-santorini.PNG',
    ...Array.from({ length: 29 }, (_, i) => `/nova-chale-santorini (${i + 1}).PNG`)
  ];
  indiceImagem = 0;
  dataEntrada: Date | null = null;
  dataSaida: Date | null = null;
  adultos = 0;
  criancas = 0;
  animais = 0;
  mostrarHospedes = false;
  mostrarCalendario = false;
  modoCalendario: 'entrada' | 'saida' = 'entrada';
  processando = false;
  dataCalendario = new Date();
  mesAtual = this.dataCalendario.getMonth();
  anoAtual = this.dataCalendario.getFullYear();
  diasCalendario: DiaCalendario[] = [];
  readonly nomesMeses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  constructor(
    private quartoService: QuartoService,
    private checkoutService: CheckoutService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private avaliacaoService: AvaliacaoService,
    private analyticsService: AnalyticsService,
    private regrasMinimoNoitesService: RegrasMinimoNoitesService
  ) {}

  ngOnInit(): void {
    this.analyticsService.quartoVisualizado('Chalé Santorini', this.quartoId);
    this.carregarQuarto();
    this.carregarDadosReserva();
    this.carregarAvaliacoes();
    this.carregarCalendario();
    this.gerarCalendario();
  }

  @HostListener('document:click', ['$event'])
  fecharMenusAoClicarFora(event: MouseEvent): void {
    const elemento = event.target as HTMLElement;
    if (!elemento.closest('.campo-reserva') && !elemento.closest('.calendario')) {
      this.mostrarHospedes = false;
      this.mostrarCalendario = false;
    }
  }

  private carregarAvaliacoes(): void {
    this.avaliacaoService.buscarAvaliacoesPublicas(this.quartoId).subscribe({
      next: avaliacoes => {
        this.avaliacoes = avaliacoes ?? [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.avaliacoes = [];
        this.cdr.detectChanges();
      }
    });
  }

  get mediaAvaliacoes(): number {
    if (!this.avaliacoes.length) return 0;
    return this.avaliacoes.reduce((soma, avaliacao) => soma + avaliacao.nota, 0) / this.avaliacoes.length;
  }

  carregarQuarto(): void {
    this.quartoService.buscarPorId(this.quartoId).subscribe({
      next: quarto => {
        this.quarto = quarto;
        this.cdr.detectChanges();
      },
      error: () => this.mostrarAlerta('error', 'Erro', 'Não foi possível carregar os dados do Chalé Santorini.')
    });
  }

  private carregarCalendario(): void {
    this.carregandoCalendario = true;
    this.erroCalendario = false;
    this.cdr.detectChanges();

    forkJoin({
      reservas: this.checkoutService.buscarDatasOcupadas(this.quartoId),
      regras: this.regrasMinimoNoitesService.listarPublicas(this.quartoId)
    }).subscribe({
      next: ({ reservas, regras }) => {
        this.reservasOcupadas = reservas ?? [];
        this.regrasMinimoNoites = regras ?? [];
        this.carregandoCalendario = false;
        this.erroCalendario = false;
        this.validarDatasRestauradas();
        this.gerarCalendario();
        this.cdr.detectChanges();
      },
      error: () => {
        this.reservasOcupadas = [];
        this.regrasMinimoNoites = [];
        this.carregandoCalendario = false;
        this.erroCalendario = true;
        this.gerarCalendario();
        this.cdr.detectChanges();
      }
    });
  }

  private validarDatasRestauradas(): void {
    if (!this.dataEntrada) return;
    if (!Number.isFinite(this.dataEntrada.getTime())) { this.dataEntrada = null; this.dataSaida = null; return; }
    if (this.dataSaida && !Number.isFinite(this.dataSaida.getTime())) this.dataSaida = null;
    if (this.dataEntrada < this.dataMinimaCheckInDate() || this.dataEstaOcupada(this.dataEntrada)) {
      this.dataEntrada = null;
      this.dataSaida = null;
      return;
    }
    if (this.dataSaida && (
      !this.checkoutMinimo ||
      this.dataSaida < this.checkoutMinimo ||
      this.periodoEstaOcupado(this.dataEntrada, this.dataSaida)
    )) {
      this.dataSaida = null;
    }
  }

  private dataEstaOcupada(data: Date): boolean {
    const dataString = this.formatarDataBackend(data);
    return this.reservasOcupadas.some(reserva => dataString >= reserva.checkIn && dataString < reserva.checkOut);
  }

  private periodoEstaOcupado(checkIn: Date, checkOut: Date): boolean {
    const entrada = this.formatarDataBackend(checkIn);
    const saida = this.formatarDataBackend(checkOut);
    return this.reservasOcupadas.some(reserva => entrada < reserva.checkOut && saida > reserva.checkIn);
  }

  private carregarDadosReserva(): void {
    const dados = this.checkoutService.obter();
    if (!dados || dados.quartoId !== this.quartoId) return;
    if (dados.checkIn) this.dataEntrada = this.converterData(dados.checkIn);
    if (dados.checkOut) this.dataSaida = this.converterData(dados.checkOut);
    if (dados.adultos !== undefined) this.adultos = dados.adultos;
    if (dados.criancas !== undefined) this.criancas = dados.criancas;
    if (dados.animais !== undefined) {
      this.animais = Math.max(
        0,
        Math.min(2, Number(dados.animais) || 0)
      );
    }
    if (this.dataEntrada) {
      this.mesAtual = this.dataEntrada.getMonth();
      this.anoAtual = this.dataEntrada.getFullYear();
    }
  }

  private converterData(data: string): Date {
    const partes = data.split('-').map(Number);
    if (partes.length !== 3 || partes.some(numero => !Number.isInteger(numero))) return new Date(NaN);
    const [ano, mes, dia] = partes;
    const resultado = new Date(ano, mes - 1, dia);
    return resultado.getFullYear() === ano && resultado.getMonth() === mes - 1 && resultado.getDate() === dia
      ? resultado : new Date(NaN);
  }

  get paginasGaleria(): number[] {
    return Array.from({ length: this.totalPaginasGaleria }, (_, i) => i);
  }
  get imagensVisiveis(): string[] {
    return this.imagensCabana.slice(this.indiceImagem, this.indiceImagem + 3);
  }
  get totalPaginasGaleria(): number {
    return Math.ceil(this.imagensCabana.length / 3);
  }
  get paginaGaleriaAtual(): number {
    return Math.floor(this.indiceImagem / 3);
  }
  imagemAnterior(): void {
    this.indiceImagem = this.indiceImagem - 3 < 0 ? (this.totalPaginasGaleria - 1) * 3 : this.indiceImagem - 3;
  }
  proximaImagem(): void {
    this.indiceImagem = this.indiceImagem + 3 >= this.imagensCabana.length ? 0 : this.indiceImagem + 3;
  }
  selecionarImagem(pagina: number): void {
    const indice = pagina * 3;
    if (indice >= 0 && indice < this.imagensCabana.length) this.indiceImagem = indice;
  }

  get totalHospedes(): number {
    return this.adultos + this.criancas;
  }
  alterarHospedes(tipo: 'adultos' | 'criancas' | 'animais', valor: number): void {
    if (tipo === 'animais') {
      this.animais = Math.max(0, Math.min(2, this.animais + valor));
      return;
    }
    const novosAdultos = tipo === 'adultos' ? Math.max(1, this.adultos + valor) : this.adultos;
    const novasCriancas = tipo === 'criancas' ? Math.max(0, this.criancas + valor) : this.criancas;
    if (this.quarto && novosAdultos + novasCriancas > this.quarto.capacidade) {
      this.mostrarAlerta('warning', 'Capacidade excedida', `Esta acomodação permite no máximo ${this.quarto.capacidade} hóspedes.`);
      return;
    }
    this.adultos = novosAdultos;
    this.criancas = novasCriancas;
  }
  toggleHospedes(): void {
    this.mostrarHospedes = !this.mostrarHospedes;
    this.mostrarCalendario = false;
  }

  abrirCalendario(modo: 'entrada' | 'saida'): void {
    this.modoCalendario = modo === 'saida' && !this.dataEntrada ? 'entrada' : modo;
    this.mostrarCalendario = true;
    this.mostrarHospedes = false;
    if (this.modoCalendario === 'saida' && this.dataEntrada) {
      const referencia = this.checkoutMinimo ?? this.dataEntrada;
      this.mesAtual = referencia.getMonth();
      this.anoAtual = referencia.getFullYear();
    }
    this.gerarCalendario();
  }

  private dataMinimaCheckInDate(): Date {
    const amanha = this.normalizarData(new Date());
    amanha.setDate(amanha.getDate() + 1);
    return amanha;
  }
  get dataMinima(): string {
    return this.formatarDataBackend(this.dataMinimaCheckInDate());
  }
  get minimoNoitesSelecionado(): number {
    if (!this.dataEntrada) return 1;
    const entrada = this.formatarDataBackend(this.dataEntrada);
    const regra = this.regrasMinimoNoites.find(item => item.ativo && item.data === entrada);
    return Math.max(1, regra?.minimoNoites ?? 1);
  }
  get checkoutMinimo(): Date | null {
    if (!this.dataEntrada) return null;
    const minimo = this.normalizarData(this.dataEntrada);
    minimo.setDate(minimo.getDate() + this.minimoNoitesSelecionado);
    return minimo;
  }

  gerarCalendario(): void {
    const primeiroDia = new Date(this.anoAtual, this.mesAtual, 1);
    const ultimoDia = new Date(this.anoAtual, this.mesAtual + 1, 0);
    const dias: DiaCalendario[] = [];
    for (let i = 0; i < primeiroDia.getDay(); i++) {
      dias.push({ numero: 0, data: new Date(0), mesAtual: false, hoje: false, selecionado: false, intervalo: false, desabilitado: true });
    }
    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      dias.push(this.criarDia(new Date(this.anoAtual, this.mesAtual, dia), true));
    }
    this.diasCalendario = dias;
  }

  criarDia(data: Date, mesAtual: boolean): DiaCalendario {
    const dia = this.normalizarData(data);
    const hoje = this.normalizarData(new Date());
    const selecionado = this.mesmaData(dia, this.dataEntrada) || this.mesmaData(dia, this.dataSaida);
    const intervalo = !!this.dataEntrada && !!this.dataSaida && dia > this.dataEntrada && dia < this.dataSaida;
    let desabilitado = !mesAtual || this.carregandoCalendario || this.erroCalendario;
    if (this.modoCalendario === 'entrada') {
      desabilitado = desabilitado || dia < this.dataMinimaCheckInDate() || this.dataEstaOcupada(dia);
    } else {
      const minimo = this.checkoutMinimo;
      desabilitado = desabilitado || !this.dataEntrada || !minimo || dia < minimo;
      if (this.dataEntrada && minimo && dia >= minimo) {
        desabilitado = desabilitado || this.periodoEstaOcupado(this.dataEntrada, dia);
      }
    }
    return { numero: data.getDate(), data: dia, mesAtual, hoje: this.mesmaData(dia, hoje), selecionado, intervalo, desabilitado };
  }

  selecionarData(dia: DiaCalendario): void {
    if (dia.numero === 0 || dia.desabilitado || this.carregandoCalendario || this.erroCalendario) return;
    if (this.modoCalendario === 'entrada') {
      this.dataEntrada = this.normalizarData(dia.data);
      this.dataSaida = null;
      this.modoCalendario = 'saida';
      const referencia = this.checkoutMinimo!;
      this.mesAtual = referencia.getMonth();
      this.anoAtual = referencia.getFullYear();
      this.gerarCalendario();
      return;
    }
    if (!this.dataEntrada || !this.checkoutMinimo) return;
    const saida = this.normalizarData(dia.data);
    if (saida < this.checkoutMinimo) {
      this.mostrarAlerta('warning', 'Estadia mínima', `Este check-in exige pelo menos ${this.minimoNoitesSelecionado} noite(s).`);
      return;
    }
    if (this.periodoEstaOcupado(this.dataEntrada, saida)) {
      this.mostrarAlerta('error', 'Período indisponível', 'Há uma reserva dentro do período selecionado.');
      return;
    }
    this.dataSaida = saida;
    this.analyticsService.datasSelecionadas(this.quartoId, this.formatarDataBackend(this.dataEntrada), this.formatarDataBackend(saida));
    this.mostrarCalendario = false;
    this.gerarCalendario();
  }

  mesAnterior(): void {
    const mesMinimo = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const atual = new Date(this.anoAtual, this.mesAtual, 1);
    if (atual <= mesMinimo) return;
    const anterior = new Date(this.anoAtual, this.mesAtual - 1, 1);
    this.mesAtual = anterior.getMonth();
    this.anoAtual = anterior.getFullYear();
    this.gerarCalendario();
  }
  mesProximo(): void {
    const proximo = new Date(this.anoAtual, this.mesAtual + 1, 1);
    this.mesAtual = proximo.getMonth();
    this.anoAtual = proximo.getFullYear();
    this.gerarCalendario();
  }
  private normalizarData(data: Date): Date {
    return new Date(data.getFullYear(), data.getMonth(), data.getDate());
  }
  mesmaData(data1: Date | null, data2: Date | null): boolean {
    return !!data1 && !!data2 && data1.getFullYear() === data2.getFullYear() && data1.getMonth() === data2.getMonth() && data1.getDate() === data2.getDate();
  }
  formatarData(data: Date | null): string {
    return data ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(data) : '';
  }
  formatarDataBackend(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }
  get quantidadeNoites(): number {
    if (!this.dataEntrada || !this.dataSaida) return 0;
    const entradaUTC = Date.UTC(this.dataEntrada.getFullYear(), this.dataEntrada.getMonth(), this.dataEntrada.getDate());
    const saidaUTC = Date.UTC(this.dataSaida.getFullYear(), this.dataSaida.getMonth(), this.dataSaida.getDate());
    return Math.round((saidaUTC - entradaUTC) / 86400000);
  }
  private obterDiaSemana(data: Date): string {
    return ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'][data.getDay()];
  }
  private calcularValorTotal(): number {
    if (!this.dataEntrada || !this.dataSaida || !this.quarto) return 0;
    if (this.adultos < 1 || this.totalHospedes > this.quarto.capacidade) return 0;
    const adicional = Number(this.quarto.valorCrianca ?? 0) * this.criancas + Number(this.quarto.valorAnimal ?? 0) * this.animais;
    let total = 0;
    for (let dia = this.normalizarData(this.dataEntrada); dia < this.dataSaida; dia.setDate(dia.getDate() + 1)) {
      const dataString = this.formatarDataBackend(dia);
      const especifica = this.quarto.tarifasData?.[dataString]?.[this.adultos];
      const padrao = this.quarto.tarifas?.[this.obterDiaSemana(dia)]?.[this.adultos];
      const tarifa = especifica ?? padrao;
      if (tarifa === undefined || tarifa === null || !Number.isFinite(Number(tarifa))) return 0;
      total += Number(tarifa) + adicional;
    }
    return total;
  }
  get valorTotal(): number {
    return this.calcularValorTotal();
  }

  continuarReserva(): void {
    if (this.processando) return;
    if (this.carregandoCalendario || this.erroCalendario) {
      this.mostrarAlerta('error', 'Calendário indisponível', 'Não foi possível confirmar as regras e a disponibilidade. Atualize a página e tente novamente.');
      return;
    }
    if (!this.dataEntrada) {
      this.mostrarAlerta('warning', 'Check-in não selecionado', 'Selecione a data de check-in.');
      return;
    }
    if (this.dataEntrada < this.dataMinimaCheckInDate()) {
      this.mostrarAlerta('warning', 'Check-in inválido', 'O check-in deve ser a partir de amanhã.');
      return;
    }
    if (!this.dataSaida) {
      this.mostrarAlerta('warning', 'Check-out não selecionado', 'Selecione a data de check-out.');
      return;
    }
    if (!this.checkoutMinimo || this.dataSaida < this.checkoutMinimo) {
      this.mostrarAlerta('warning', 'Estadia mínima não atendida', `Para o check-in em ${this.formatarData(this.dataEntrada)}, são necessárias pelo menos ${this.minimoNoitesSelecionado} noite(s). O primeiro check-out permitido é ${this.formatarData(this.checkoutMinimo)}.`);
      return;
    }
    if (this.periodoEstaOcupado(this.dataEntrada, this.dataSaida)) {
      this.mostrarAlerta('error', 'Chalé indisponível', 'Há uma reserva dentro do período selecionado. Escolha outras datas.');
      return;
    }
    if (this.adultos < 1) {
      this.mostrarAlerta('warning', 'Hóspede obrigatório', 'É necessário ter pelo menos 1 adulto.');
      return;
    }
    if (!this.quarto || this.totalHospedes > this.quarto.capacidade) {
      this.mostrarAlerta('warning', 'Capacidade excedida', 'A quantidade de hóspedes excede a capacidade ou os dados do quarto ainda não foram carregados.');
      return;
    }
    const valorTotal = this.calcularValorTotal();
    if (valorTotal <= 0) {
      this.mostrarAlerta('error', 'Tarifa indisponível', 'Não foi possível calcular o valor da hospedagem.');
      return;
    }
    const checkIn = this.formatarDataBackend(this.dataEntrada);
    const checkOut = this.formatarDataBackend(this.dataSaida);
    this.processando = true;
    this.checkoutService.verificarDisponibilidade(this.quartoId, checkIn, checkOut).subscribe({
      next: resposta => {
        this.processando = false;
        if (!resposta.disponivel) {
          this.carregarCalendario();
          this.mostrarAlerta('error', 'Chalé indisponível', 'O Chalé Santorini não está disponível para o período informado.');
          this.cdr.detectChanges();
          return;
        }
        this.checkoutService.atualizar({
          quartoId: this.quartoId,
          quartoNome: this.quarto?.nome,
          quartoImagem: this.imagensCabana[0],
          checkIn,
          checkOut,
          adultos: this.adultos,
          criancas: this.criancas,
          animais: this.animais,
          quantidadeHospedes: this.totalHospedes,
          quantidadeNoites: this.quantidadeNoites,
          valorTotal
        });
        this.router.navigate(['/checkout/hospedes']);
        this.cdr.detectChanges();
      },
      error: () => {
        this.processando = false;
        this.mostrarAlerta('error', 'Erro ao verificar disponibilidade', 'Não foi possível verificar a disponibilidade do Chalé Santorini. Tente novamente.');
        this.cdr.detectChanges();
      }
    });
  }

  private mostrarAlerta(icon: 'success' | 'error' | 'warning' | 'info' | 'question', title: string, text: string): void {
    Swal.fire({ icon, title, text, confirmButtonText: 'OK', confirmButtonColor: '#596238', customClass: { popup: 'swal-cabana' } });
  }
}
